import React, { useEffect, useState } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { reorderList } from "./SegmentEditor";
import {
  STRENGTH_BLOCK_KINDS,
  mkStrengthBlock,
  mkStrengthExercise,
} from "../../lib/coaching";
import { listStrengthExerciseNames } from "../../lib/api";

/**
 * A single exercise row (inside a block).
 */
function ExerciseRow({ ex, onChange, onDelete, dragHandleProps }) {
  const update = (patch) => onChange({ ...ex, ...patch });
  // Back-compat: treat legacy rpe-with-no-kind as "rpe"
  const kind = ex.intensity_kind || (ex.rpe ? "rpe" : "");
  return (
    <div className="grid grid-cols-12 gap-2 items-start py-2 border-b border-[var(--tir-border)] last:border-b-0">
      <div className="col-span-12 md:col-span-4 flex items-center gap-1.5">
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] cursor-grab active:cursor-grabbing shrink-0"
            aria-label="Drag to reorder exercise"
            title="Drag to reorder (or move to another block)"
            data-testid={`exercise-drag-handle-${ex.id}`}
          >
            <GripVertical size={14} />
          </span>
        )}
        <input
          value={ex.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="Exercise — e.g. Back squat"
          list="tir-exercise-library"
          data-testid="strength-exercise-name"
          className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm focus:outline-none"
        />
      </div>
      <div className="col-span-3 md:col-span-1">
        <label className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] block">
          Sets
        </label>
        <input
          value={ex.sets || ""}
          onChange={(e) => update({ sets: e.target.value })}
          placeholder="3"
          className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm text-center font-mono-tir focus:outline-none"
        />
      </div>
      <div className="col-span-3 md:col-span-1">
        <label className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] block">
          Reps
        </label>
        <input
          value={ex.reps || ""}
          onChange={(e) => update({ reps: e.target.value })}
          placeholder="8-12"
          className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm text-center font-mono-tir focus:outline-none"
        />
      </div>
      <div className="col-span-6 md:col-span-2">
        <label className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] block">
          Intensity
        </label>
        <div className="flex gap-1">
          <select
            value={kind}
            onChange={(e) => {
              const k = e.target.value || null;
              // If clearing the kind, also clear the value
              update({ intensity_kind: k, rpe: k ? ex.rpe : "" });
            }}
            data-testid="strength-intensity-kind"
            className="bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-xs focus:outline-none"
          >
            <option value="">—</option>
            <option value="rpe">RPE</option>
            <option value="rir">RIR</option>
          </select>
          <input
            value={ex.rpe || ""}
            onChange={(e) => update({ rpe: e.target.value })}
            placeholder={kind === "rir" ? "2" : kind === "rpe" ? "7" : ""}
            disabled={!kind}
            className="flex-1 bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm text-center font-mono-tir focus:outline-none disabled:opacity-40"
          />
        </div>
      </div>
      <div className="col-span-3 md:col-span-1">
        <label className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] block">
          Rest
        </label>
        <input
          value={ex.rest || ""}
          onChange={(e) => update({ rest: e.target.value })}
          placeholder="90s"
          className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm text-center font-mono-tir focus:outline-none"
        />
      </div>
      <div className="col-span-11 md:col-span-2">
        <label className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] block">
          Notes
        </label>
        <input
          value={ex.notes || ""}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="Tempo, cues, etc."
          className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-xs focus:outline-none"
        />
      </div>
      <div className="col-span-1 flex items-end justify-end pb-1">
        <button
          type="button"
          onClick={onDelete}
          className="text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] p-1"
          aria-label="Delete exercise"
        >
          <Trash2 size={13} />
        </button>
      </div>
      {/* Coach-defined swap alternatives. Stored as an array of free-text
          names but we keep a local raw string while the coach is typing
          so mid-word spaces don't get stripped by an on-every-keystroke
          .trim() (bug reported 2026-06). The array is rebuilt on blur. */}
      <div className="col-span-12 -mt-1">
        <label className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] block">
          Swap options for athlete <span className="text-[var(--tir-muted)] normal-case tracking-normal italic">(optional · comma-separated)</span>
        </label>
        <SwapOptionsInput
          value={ex.alternatives || []}
          onCommit={(list) => update({ alternatives: list })}
        />
      </div>
    </div>
  );
}

/**
 * SwapOptionsInput — controlled input that lets the coach type freely
 * (including trailing spaces + mid-word spaces) without the parent
 * normalising on every keystroke. Splits into the alternatives array
 * only on blur — the moment the field is truly "done" being edited.
 *
 * Also handles Enter (behaves like blur) so hitting return commits
 * without needing to click away.
 */
function SwapOptionsInput({ value, onCommit }) {
  const [draft, setDraft] = React.useState(() => (value || []).join(", "));
  // Re-sync the raw text if the parent-provided array changes from
  // outside (e.g. loading a different exercise). We compare the joined
  // form so typing "Push, " (mid-edit) doesn't cause a self-loop with
  // the parent's [Push] array.
  React.useEffect(() => {
    const parentJoined = (value || []).join(", ");
    const draftNormalized = draft
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
    if (parentJoined !== draftNormalized) {
      setDraft(parentJoined);
    }
  }, [value]);
  const commit = () => {
    const list = draft
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    onCommit(list);
  };
  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
          e.currentTarget.blur();
        }
      }}
      placeholder="e.g. Push-up, Dumbbell Bench Press, Floor Press"
      data-testid="strength-exercise-alternatives"
      className="w-full bg-transparent border-0 border-b border-dashed border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-xs focus:outline-none"
    />
  );
}

/**
 * One strength block (straight sets, superset, circuit, AMRAP, EMOM, For time).
 */
function StrengthBlockCard({ block, onChange, onDelete, dragHandleProps }) {
  const update = (patch) => onChange({ ...block, ...patch });

  const updateEx = (idx, updated) =>
    update({ exercises: block.exercises.map((e, i) => (i === idx ? updated : e)) });

  const deleteEx = (idx) =>
    update({ exercises: block.exercises.filter((_, i) => i !== idx) });

  const addEx = () => update({ exercises: [...block.exercises, mkStrengthExercise()] });

  const needsRounds = ["circuit", "amrap", "emom"].includes(block.kind);
  const needsTimeCap = ["amrap", "for_time"].includes(block.kind);

  return (
    <div className="border-2 border-[var(--tir-ink)] bg-[var(--tir-ivory)] p-4">
      <div className="flex flex-wrap items-center gap-3 mb-3 pb-3 border-b border-[var(--tir-border)]">
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] cursor-grab active:cursor-grabbing -ml-1"
            aria-label="Drag to reorder block"
            title="Drag to reorder block"
            data-testid={`block-drag-handle-${block.id}`}
          >
            <GripVertical size={16} />
          </span>
        )}
        <input
          value={block.label || ""}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Block A"
          className="font-serif-display text-lg text-[var(--tir-ink)] bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 focus:outline-none w-24"
        />
        <select
          value={block.kind}
          onChange={(e) => update({ kind: e.target.value })}
          data-testid="strength-block-kind"
          className="bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2 py-1 text-sm focus:outline-none"
        >
          {STRENGTH_BLOCK_KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
        {needsRounds && (
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
              Rounds
            </span>
            <input
              type="number"
              min="1"
              max="50"
              value={block.rounds ?? ""}
              onChange={(e) =>
                update({ rounds: e.target.value === "" ? null : Number(e.target.value) })
              }
              className="w-14 bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2 py-1 text-center font-mono-tir text-sm focus:outline-none"
            />
          </div>
        )}
        {needsTimeCap && (
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
              Time cap (min)
            </span>
            <input
              type="number"
              min="1"
              value={block.time_cap_minutes ?? ""}
              onChange={(e) =>
                update({
                  time_cap_minutes: e.target.value === "" ? null : Number(e.target.value),
                })
              }
              className="w-14 bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2 py-1 text-center font-mono-tir text-sm focus:outline-none"
            />
          </div>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="ml-auto text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] p-1"
            aria-label="Delete block"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <Droppable droppableId={`block-${block.id}`} type="exercise">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`min-h-[8px] ${snapshot.isDraggingOver ? "bg-[var(--tir-burgundy)]/5" : ""}`}
            data-testid={`block-exercises-${block.id}`}
          >
            {(block.exercises || []).map((ex, i) => (
              <Draggable key={ex.id || `i${i}`} draggableId={`ex-${ex.id || `${block.id}-${i}`}`} index={i}>
                {(dp, snap) => (
                  <div ref={dp.innerRef} {...dp.draggableProps} className={snap.isDragging ? "bg-white shadow-lg ring-2 ring-[var(--tir-burgundy)]/40 px-2" : ""}>
                    <ExerciseRow
                      ex={ex}
                      onChange={(u) => updateEx(i, u)}
                      onDelete={() => deleteEx(i)}
                      dragHandleProps={dp.dragHandleProps}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[var(--tir-border)]">
        <button
          type="button"
          onClick={addEx}
          data-testid="add-exercise-btn"
          className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
        >
          <Plus size={12} /> Exercise
        </button>
        <input
          value={block.notes || ""}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="Block notes (optional)"
          className="flex-1 bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-xs italic focus:outline-none"
        />
      </div>
    </div>
  );
}

/**
 * Full strength-workout builder. Manages a list of strength blocks.
 *
 * Props:
 *   blocks: StrengthBlock[]
 *   onChange: (StrengthBlock[]) => void
 */
export default function StrengthEditor({ blocks, onChange }) {
  const update = (idx, updated) =>
    onChange(blocks.map((b, i) => (i === idx ? updated : b)));
  const deleteBlock = (idx) => onChange(blocks.filter((_, i) => i !== idx));
  const addBlock = (kind = "regular") =>
    onChange([...blocks, mkStrengthBlock({ kind })]);

  const onDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    if (type === "block") {
      onChange(reorderList(blocks, source.index, destination.index));
      return;
    }
    if (type === "exercise") {
      const fromIdx = blocks.findIndex((b) => `block-${b.id}` === source.droppableId);
      const toIdx = blocks.findIndex((b) => `block-${b.id}` === destination.droppableId);
      if (fromIdx < 0 || toIdx < 0) return;
      if (fromIdx === toIdx) {
        const b = blocks[fromIdx];
        update(fromIdx, { ...b, exercises: reorderList(b.exercises, source.index, destination.index) });
        return;
      }
      // Move an exercise to a different block.
      const fromEx = Array.from(blocks[fromIdx].exercises || []);
      const [moved] = fromEx.splice(source.index, 1);
      const toEx = Array.from(blocks[toIdx].exercises || []);
      toEx.splice(destination.index, 0, moved);
      onChange(
        blocks.map((b, i) => (i === fromIdx ? { ...b, exercises: fromEx } : i === toIdx ? { ...b, exercises: toEx } : b))
      );
    }
  };

  const [libraryNames, setLibraryNames] = useState([]);
  useEffect(() => {
    let alive = true;
    listStrengthExerciseNames()
      .then((names) => {
        if (alive) setLibraryNames(names || []);
      })
      .catch(() => {
        /* ignore — autocomplete is best-effort */
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div>
      <datalist id="tir-exercise-library">
        {libraryNames.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      <div className="flex items-center justify-between mb-3">
        <div className="font-serif-display text-lg text-[var(--tir-ink)]">Blocks</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => addBlock("regular")}
            data-testid="add-regular-block-btn"
            className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
          >
            + Straight sets
          </button>
          <button
            type="button"
            onClick={() => addBlock("superset")}
            className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
          >
            + Superset
          </button>
          <button
            type="button"
            onClick={() => addBlock("circuit")}
            className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
          >
            + Circuit
          </button>
          <button
            type="button"
            onClick={() => addBlock("amrap")}
            className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
          >
            + AMRAP
          </button>
          <button
            type="button"
            onClick={() => addBlock("emom")}
            className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
          >
            + EMOM
          </button>
        </div>
      </div>
      {(blocks || []).length === 0 ? (
        <div className="border border-dashed border-[var(--tir-border)] py-8 text-center text-sm text-[var(--tir-muted)]">
          No blocks yet. Add one above.
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="strength-blocks" type="block">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4" data-testid="strength-blocks-list">
                {blocks.map((b, i) => (
                  <Draggable key={b.id || `b${i}`} draggableId={`block-${b.id || i}`} index={i}>
                    {(dp, snapshot) => (
                      <div ref={dp.innerRef} {...dp.draggableProps} className={snapshot.isDragging ? "shadow-xl ring-2 ring-[var(--tir-burgundy)]/40" : ""}>
                        <StrengthBlockCard
                          block={b}
                          onChange={(u) => update(i, u)}
                          onDelete={() => deleteBlock(i)}
                          dragHandleProps={dp.dragHandleProps}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}
